/**
 * PortfoliYo Lab — read a definition without adopting it.
 *
 * **The gap this closes.** Every other way to look at a strategy in this product requires the
 * strategy to exist first. Allocation Analysis picks one from a dropdown of your saved rows; the
 * editor's tree is the tree of the thing you are about to create. So the definitions hardest to
 * reason about — one somebody sent you, one you are half-way through writing, a portfolio whose
 * sleeves you have not written yet — were the ones you could not look at without first creating a
 * row to look at them with. Creating a strategy to read a strategy is a workflow that leaves
 * debris: rows that were never meant to be deployed, listed forever beside the ones that were.
 *
 * **Nothing here writes.** The page holds text, posts it, and draws the answer. There is no create
 * button, no id, no version, no name field — and that absence is the feature, not an unfinished
 * edge. Authoring lives one nav item away, and a definition that turns out to be worth keeping is
 * pasted into it.
 *
 * **One textarea, not a mode selector.** `StrategySourceSelector` exists because a strategy being
 * CREATED must be stored as one kind or the other, and the two differ in their identity rule and in
 * five stored columns. Nothing is stored here, so the only thing a mode would decide is which
 * parser runs — and the server can already tell from the text. Asking a question whose answer is
 * checkable, and then acting on the answer given rather than the true one, only turns "here is your
 * portfolio" into a parse error.
 *
 * **The tree is `LogicTree`, unchanged in what it means.** Allocation Analysis draws one symbol's
 * branches from a saved strategy; this draws a whole definition. Same vocabulary, so the same
 * component — which is also why the highlight box is worth having: it is that page's lens, brought
 * to a definition that has no row to hang it on.
 *
 * **A 30 MB definition must not reach the textarea, and that is not a detail.** The strategies this
 * page exists for are minified Composer exports: `VRK_Borg_VIX_RSI_Cascade.json` is 30.4 MB on a
 * SINGLE line with no newline in it. A `<textarea>` does not virtualize — handed that string it
 * lays out one thirty-million-character wrapped line and the tab stops responding, before a single
 * request has been made. So the full text lives in state that only the REQUEST reads, and the
 * textarea shows a bounded slice of it. Above `EDITABLE_MAX_CHARS` it stops claiming to be an
 * editor and says so, because a file nobody can hand-edit should not be presented as editable.
 *
 * **Highlighting is computed here, from the tree already on screen.** It used to be a request
 * parameter, which quietly made every keystroke in the highlight box re-upload the definition and
 * re-parse it server-side — 30 MB and 3.5 seconds to repaint some pills. Marking is a display
 * choice and is now made at display time. The trade is honest and worth naming: this walk is
 * TypeScript, and this repository has no frontend test runner, so logic that had two tests in
 * Python now has none. It is a dozen lines of "does this subtree mention X", the pill half of the
 * same question was always client-side in `AssetPill`, and the alternative was a page that locks up
 * when you type.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, AlertTriangle, CheckCircle2, Eraser, Network, Scissors } from 'lucide-react';
import { LogicTree } from '../components/LogicTree';
import { markHighlight, pickExpandedDepth, RENDER_BUDGET_NODES } from './treeDisplay';
// Shared with the New Strategy page: both learned the same lesson from the same 30 MB file.
import { EDITABLE_MAX_CHARS, PREVIEW_CHARS, describeSize } from './definitionText';
import {
  labStrategyApi,
  LabStrategyError,
  type CompositeTree,
  type DeclaredSleeve,
  type DefinitionTree,
} from './labStrategyApi';
import { DefinitionFileInput } from './strategies/DefinitionFileInput';

/** Long enough that a paste settles, short enough that it feels like the page answering back. */
const DEBOUNCE_MS = 500;

const KIND_LABELS: Record<string, string> = {
  strategy: 'Composer strategy',
  portfolio: 'Portfolio document',
  unknown: 'Unrecognised',
};

const SAMPLE =
  '(defsymphony "Scratch" {:description "Paste over me."}\n' +
  '  (weight-specified\n' +
  '    0.6 (group "Trend"\n' +
  '          [(if (> (current-price "SPY") (moving-average-price "SPY" {:window 200}))\n' +
  '               [(weight-equal [(asset "SPY" "S&P 500") (asset "QQQ" "Nasdaq 100")])]\n' +
  '               [(weight-equal [(asset "BIL" "T-Bills")])])])\n' +
  '    0.4 (group "Rotation"\n' +
  '          [(filter (rsi {:window 14}) (select-top 2)\n' +
  '             [(asset "XLK" "Tech") (asset "XLV" "Health Care") (asset "XLU" "Utilities")])])))';

function SleeveTable({ sleeves }: { sleeves: DeclaredSleeve[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead className="text-slate-500">
          <tr>
            <th className="py-1 pr-3 font-medium">Key</th>
            <th className="py-1 pr-3 font-medium">Name</th>
            <th className="py-1 font-medium">Root id</th>
          </tr>
        </thead>
        <tbody className="align-top">
          {sleeves.map((sleeve) => (
            <tr key={sleeve.key} className="border-t border-slate-100">
              <td className="py-1 pr-3 font-mono text-slate-800">{sleeve.key}</td>
              <td className="py-1 pr-3 text-slate-600">{sleeve.name ?? '—'}</td>
              <td className="py-1 font-mono text-[11px] text-slate-400">{sleeve.root_id ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Said out loud, because the natural reading of a sleeve list is that these resolved. */}
      <p className="mt-2 text-xs text-slate-500">
        As the document declares them. Nothing was looked up — a sleeve listed here need not exist
        as one of your strategies yet.
      </p>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <h2 className="mb-3 text-sm font-semibold text-slate-900">{title}</h2>
      {children}
    </section>
  );
}

function CompositeCard({
  composite,
  highlight,
  expandedDepth,
}: {
  composite: CompositeTree;
  highlight: string;
  expandedDepth: number;
}) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <p className="mb-2 text-xs">
        <span className="font-mono font-semibold text-slate-800">{composite.key}</span>
        {composite.name && <span className="ml-2 text-slate-500">{composite.name}</span>}
      </p>
      {composite.tree ? (
        <LogicTree
          node={composite.tree}
          symbol={highlight}
          defaultExpandedDepth={expandedDepth}
        />
      ) : (
        <p className="text-xs italic text-slate-400">Nothing to draw.</p>
      )}
    </div>
  );
}

export function DefinitionVisualizer() {
  /**
   * The full definition — posted, never rendered. Holding thirty megabytes in React state costs
   * nothing; handing the same string to a DOM node is what hangs the tab.
   */
  const [definition, setDefinition] = useState('');
  /** What the textarea shows: the definition itself, or a bounded slice when it is too big. */
  const [editorText, setEditorText] = useState('');
  const [highlight, setHighlight] = useState('');
  const [result, setResult] = useState<DefinitionTree | null>(null);
  const [reading, setReading] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const oversized = definition.length > EDITABLE_MAX_CHARS;

  // Guards the request, not the render: an empty page must not post an empty string and then show
  // "Nothing to read." to somebody who has not typed anything yet.
  const empty = definition.trim() === '';

  /** Typing. The two stay identical while the definition is small enough to be an editor. */
  const edit = (next: string) => {
    setDefinition(next);
    setEditorText(next);
  };

  /**
   * A file arriving. This is the path that used to hang the page, so it is the one that decides
   * whether the textarea gets the text or a slice of it.
   */
  const load = (text: string) => {
    setDefinition(text);
    setEditorText(
      text.length > EDITABLE_MAX_CHARS ? text.slice(0, PREVIEW_CHARS) : text,
    );
  };

  const read = useCallback(async (text: string) => {
    setReading(true);
    setFailure(null);
    try {
      setResult(await labStrategyApi.definitionTree(text));
    } catch (error) {
      // A transport or auth failure — distinct from "this definition does not parse", which comes
      // back as a perfectly successful response with `valid: false`.
      setFailure(error instanceof LabStrategyError ? error.message : 'Could not reach the server.');
      setResult(null);
    } finally {
      setReading(false);
    }
  }, []);

  // Debounced, and keyed on the DEFINITION alone. It used to depend on `highlight` too, which
  // meant every keystroke in the highlight box re-uploaded the definition and re-parsed it — 30 MB
  // and 3.5 seconds, to change which pills are green. Marking happens below, on the tree we have.
  const latest = useRef(0);
  useEffect(() => {
    if (empty) {
      setResult(null);
      return;
    }
    const ticket = ++latest.current;
    const timer = setTimeout(() => {
      // A slow read for text that has since been replaced must not overwrite a fast one for the
      // text on screen. Out-of-order responses are the normal case when somebody pastes over a
      // large definition.
      if (ticket === latest.current) read(definition);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [definition, empty, read]);

  const tooLargeToDraw = result?.truncated === true;

  // Re-marked when the highlight changes; re-walked when a new tree arrives. Never a request.
  const markedTree = useMemo(
    () => markHighlight(result?.tree ?? null, highlight),
    [result, highlight],
  );
  const markedComposites = useMemo(
    () =>
      (result?.composites ?? []).map((composite) => ({
        ...composite,
        tree: markHighlight(composite.tree, highlight),
      })),
    [result, highlight],
  );

  // Deep branches fold on a big tree. `LogicTree` renders nothing inside a folded branch, so this
  // is what keeps a 3,661-node drawing off the DOM until somebody asks for part of it. Keyed on
  // the response, not on the marked tree — marking changes flags, never shape.
  const expandedDepth = useMemo(
    () => pickExpandedDepth(result?.tree ?? null, RENDER_BUDGET_NODES),
    [result],
  );

  return (
    <div className="space-y-4">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
          <Network className="h-5 w-5 text-emerald-700" aria-hidden="true" />
          Visualizer
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Paste or upload a Composer definition or a <code className="font-mono">vrk-portfolio/v1</code>{' '}
          document and read it as a tree. Nothing is saved — no strategy is created, no sleeve is
          looked up. To keep one, take it to{' '}
          <Link to="/strategies" className="font-medium text-emerald-800 underline-offset-2 hover:underline">
            Strategies
          </Link>
          .
        </p>
      </header>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        {/* ── Input ─────────────────────────────────────────────────────────────────────── */}
        <div className="space-y-4">
          <Card title="Definition">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <DefinitionFileInput
                id="visualizer-file"
                label="Upload"
                accept=".txt,.clj,.edn,.json"
                onLoad={load}
              />
              <button
                type="button"
                onClick={() => edit(SAMPLE)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Load sample
              </button>
              <button
                type="button"
                disabled={empty}
                onClick={() => {
                  edit('');
                  setHighlight('');
                }}
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-slate-500 hover:bg-slate-100 disabled:opacity-40"
              >
                <Eraser className="h-4 w-4" aria-hidden="true" /> Clear
              </button>
            </div>
            <textarea
              value={editorText}
              onChange={(event) => edit(event.target.value)}
              readOnly={oversized}
              rows={22}
              spellCheck={false}
              placeholder="(defsymphony …)   or   { &quot;schema&quot;: &quot;vrk-portfolio/v1&quot;, … }"
              aria-label="Definition to visualize"
              className={`w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs ${
                oversized ? 'bg-slate-50 text-slate-600' : ''
              }`}
            />
            {oversized ? (
              /* The whole file WAS read and is what gets drawn — only the box is showing a slice.
                 Said plainly, because a truncated preview that looked like the input would make
                 the reader think half their strategy went missing. */
              <p className="mt-1 text-xs text-amber-800">
                {describeSize(definition)} — too large to edit here. Showing the first{' '}
                {PREVIEW_CHARS.toLocaleString()} characters; the whole file was read and is what the
                tree below describes.
              </p>
            ) : (
              <p className="mt-1 text-xs text-slate-500">
                Composer DSL, a Composer JSON export, or a portfolio document. Which one it is, is
                read off the text.
              </p>
            )}
          </Card>

          <Card title="Highlight">
            <input
              value={highlight}
              onChange={(event) => setHighlight(event.target.value)}
              list="visualizer-candidates"
              placeholder={result?.kind === 'portfolio' ? 'Sleeve or composite key' : 'Ticker'}
              aria-label="Ticker or sleeve key to highlight"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <datalist id="visualizer-candidates">
              {(result?.candidates ?? []).map((candidate) => (
                <option key={candidate} value={candidate} />
              ))}
            </datalist>
            <p className="mt-1 text-xs text-slate-500">
              Marks every branch that can allocate it — the Allocation Analysis lens, on a
              definition that has no row.
            </p>
          </Card>
        </div>

        {/* ── Output ────────────────────────────────────────────────────────────────────── */}
        <div className="space-y-4">
          {failure && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <p className="flex items-center gap-2 font-semibold">
                <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                {failure}
              </p>
            </div>
          )}

          {empty && !failure && (
            <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
              Nothing to draw yet. Paste a definition, or load the sample.
            </div>
          )}

          {result && (
            <>
              <Card title="What this is">
                <div className="space-y-2 text-sm">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                      {KIND_LABELS[result.kind] ?? result.kind}
                    </span>
                    {result.valid ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-800">
                        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> Reads cleanly
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-800">
                        <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" /> Has problems
                      </span>
                    )}
                    {reading && <span className="ml-auto text-xs text-slate-400">Reading…</span>}
                  </p>
                  {result.name && (
                    <p className="font-medium text-slate-900">{result.name}</p>
                  )}
                  {result.description && (
                    <p className="text-xs leading-relaxed text-slate-600">{result.description}</p>
                  )}
                  {result.error && (
                    <p className="whitespace-pre-wrap rounded-lg bg-amber-50 px-3 py-2 font-mono text-xs text-amber-900">
                      {result.error}
                    </p>
                  )}
                </div>
              </Card>

              {result.sleeves.length > 0 && (
                <Card title={`Sleeves (${result.sleeves.length})`}>
                  <SleeveTable sleeves={result.sleeves} />
                </Card>
              )}

              {tooLargeToDraw && (
                <div
                  role="status"
                  className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                >
                  <Scissors className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>
                    Too large to draw in full — showing the first {result.node_count.toLocaleString()}{' '}
                    nodes. Monolithic exports run to six figures; the portfolio form of the same
                    strategy is the readable one.
                  </span>
                </div>
              )}

              {markedTree ? (
                <Card title="Allocation">
                  <LogicTree
                    node={markedTree}
                    symbol={highlight}
                    defaultExpandedDepth={expandedDepth}
                  />
                </Card>
              ) : (
                result.valid && (
                  <Card title="Allocation">
                    <p className="text-sm text-slate-500">
                      This definition parses but allocates nothing.
                    </p>
                  </Card>
                )
              )}

              {result.composites.length > 0 && (
                <Card title={`Composites (${result.composites.length})`}>
                  {/* Drawn once each, beside the tree rather than inside it. A composite exists
                      because many leaves reference it; inlining would redraw it at every one. */}
                  <p className="mb-3 text-xs text-slate-500">
                    Reusable sub-allocations. Leaves above reference these by key.
                  </p>
                  <div className="space-y-3">
                    {markedComposites.map((composite) => (
                      <CompositeCard
                        key={composite.key}
                        composite={composite}
                        highlight={highlight}
                        expandedDepth={expandedDepth}
                      />
                    ))}
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
